<template>
  <dialogue-modal
    v-if="productToDelete"
    title="Are you sure you want to send it to recovery?"
    :message="productToDelete.name"
    :close-popup="hideDeleteModal"
    :buttons="buttons"
  />
  <page-title pageTitle="Products" title="All Products" />

  <div class="row pb-4 gy-3">
    <div class="col-sm-auto col-md">
      <router-link to="/products/new" class="btn btn-primary addtax-modal">
        <i class="las la-plus me-1"></i> Add New
      </router-link>
    </div>

    <div class="col-sm-auto col-md-6">
      <div>
        <div class="search-box">
          <input
            type="text"
            class="form-control"
            id="searchMemberList"
            placeholder="Search for Result"
            v-model="searchQuery"
            @input="goToPage(1)"
          />
          <i class="las la-search search-icon"></i>
        </div>
      </div>
    </div>

    <div class="col-sm-auto">
      <div class="d-flex input-group">
        <label class="input-group-text" for="inputGroupSelect01">Show</label>
        <select
          class="form-select"
          id="resultsPerPage"
          v-model="resultsPerPage"
          @change="goToPage(1)"
        >
          <option
            v-for="option in resultsPerPageOptions"
            :key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>
      </div>
    </div>
  </div>

  <div v-if="data.length > 0">
    <div class="row">
      <div class="col-xl-12">
        <div class="card">
          <div class="card-body">
            <table-component
              :headers="headers"
              :data="formattedData"
              :actions="actions"
              :delete-selected="deleteSelectedProducts"
              :offset="(currentPage - 1) * resultsPerPage"
              :recoverable="true"
              @sort="goToPage"
            />
          </div>
        </div>
      </div>
    </div>
    <pagination-component
      :rows-loaded="data.length"
      :current-page="currentPage"
      :num-pages="numPages"
      :total-rows="totalRows"
      :go-to-page="goToPage"
    />
  </div>

  <div v-else class="row">
    <div class="col-xl-12">
      <div class="card">
        <LoadingModal v-if="loading" />
        <div v-else class="card-body">
          {{ error }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageTitleComponent from "@/components/shared/PageTitle";
import TableComponent from "@/components/utils/Table";
import { SETTINGS } from "@/constants/config";
import { CONSTANTS } from "@/constants/constants";
import LoadingModalComponent from "@/components/utils/LoadingModal";
import PaginationComponent from "@/components/utils/paginationComponent";
import DialogueModal from "@/components/utils/modals/DialogueModal";

export default {
  name: "AllProductsView",
  components: {
    DialogueModal,
    PaginationComponent,
    PageTitle: PageTitleComponent,
    TableComponent: TableComponent,
    LoadingModal: LoadingModalComponent,
  },
  data() {
    return {
      searchQuery: "",
      resultsPerPage: SETTINGS.resultsPerPage,
      productToDelete: null,
    };
  },
  computed: {
    headers() {
      return CONSTANTS.PRODUCTS_HEADERS;
    },
    formattedData() {
      const allProducts = this.$store.getters.allProducts;
      return allProducts.map((product) => ({
        ...product,
        costPrice: this.priceFormat(product.costPrice),
        sellingPrice: this.priceFormat(product.sellingPrice),
        suggestedRetailPrice: this.priceFormat(product.suggestedRetailPrice),
      }));
    },
    data() {
      return this.$store.getters.allProducts;
    },
    currentPage() {
      return this.$store.getters.productsCurrentPage;
    },
    numPages() {
      return this.$store.getters.productsNumPages;
    },
    totalRows() {
      return this.$store.getters.productsTotalRows;
    },
    resultsPerPageOptions() {
      const options = CONSTANTS.RESULTS_PER_PAGE_OPTIONS;
      if (!options.includes(this.resultsPerPage)) {
        options.unshift(this.resultsPerPage);
      }
      return options;
    },
    actions() {
      return {
        View: (product) => this.viewProduct(product.id),
        Edit: (product) => this.editProduct(product.id),
        Delete: (product) => this.showDeleteModal(product),
      };
    },
    error() {
      return this.$store.getters.productsError;
    },
    loading() {
      return this.$store.getters.productsLoading;
    },
    buttons() {
      return {
        Yes: {
          class: "btn-danger",
          function: () => this.deleteProduct(),
        },
        No: {
          class: "btn-light px-3",
          function: () => this.hideDeleteModal(),
        },
      };
    },
  },
  methods: {
    priceFormat(price) {
      return `$${price
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`.replace("$-", "-$");
    },
    async goToPage(page, sortBy = null) {
      let filter = {
        sortingList: sortBy,
        currentPage: page,
        resultsPerPage:
          this.resultsPerPage === "ALL" ? this.totalRows : this.resultsPerPage,
        searchQuery: this.searchQuery,
      };
      await this.$store.dispatch("getProducts", filter);
    },
    viewProduct(id) {
      this.$router.push("/products/" + id);
    },
    editProduct(id) {
      this.$router.push("/products/edit/" + id);
    },
    showDeleteModal(product) {
      this.productToDelete = product;
    },
    async deleteProduct() {
      if (this.productToDelete) {
        await this.$store.dispatch("deleteProduct", this.productToDelete.id);
        await this.goToPage(1);
        this.hideDeleteModal();
      } else {
        alert("There has been an error");
      }
    },
    async deleteSelectedProducts(products) {
      await this.$store.dispatch("deleteSelectedProducts", products);
      await this.goToPage(1);
    },
    hideDeleteModal() {
      this.productToDelete = null;
    },
  },
  async created() {
    let filter = {
      resultsPerPage: SETTINGS.resultsPerPage,
    };
    await this.$store.dispatch("getProducts", filter);
  },
  async beforeUnmount() {
    await this.$store.dispatch("resetProducts");
  },
};
</script>

<style scoped></style>
